/**
 * Video loading and playback control for the generic media feature (sections/soulog-media-feature.liquid) (no dependencies).
 *
 * Why this exists (2026-09-15):
 * Originally every video in this section was autoplay + a real src. With autoplay, preload doesn't count;
 * the browser must buffer in order to autoplay — the sense video mid-way down the product page (2.67MB) was fully downloaded on page open,
 * without the user scrolling at all. Now Liquid keeps autoplay only for the first section on the page (the above-the-fold KV);
 * the rest output preload="none" + the smf__video--lazy marker, and this file decides when to download and when to play:
 *
 *   · Lazy videos: when 1 screen away from the viewport, raise preload and load(); play() on entering the viewport; pause() on leaving.
 *   · Above-the-fold video: already downloading via autoplay, so don't touch loading (load() would reset progress);
 *     but it is likewise pause()d when it leaves the viewport — a looping video keeps decoding off-screen, wasting battery on phones.
 *   · Old browsers without IntersectionObserver: load and play directly — better to download extra than to not play.
 *
 * ## smf__video--at-top — "start the show only once scrolled to the top" (theme editor toggle "Start playing only when scrolled to top")
 *
 * The default behavior is "play as soon as one edge is visible" (IO default threshold 0). For a performance animation with a beginning and end, that's wrong:
 * the animation starts the moment the section peeks in from the bottom of the screen, and by the time the user actually scrolls it into view, they're seeing the middle of the show.
 * For videos with this class, playback is deferred until "the element's top edge scrolls into the top 10% of the viewport" (TOP_GATE_MARGIN).
 * 10% rather than an exact 0: first, it leaves room for a sticky header at the top; second, a zero-height IO root fails to detect intersection in some browsers.
 *
 * Once released, the video is handed back to the regular observer and behaves like the other videos (pause on leaving, resume on returning) —
 * the gate only controls when it starts the **first** time. Until it reaches the top it sits on the first frame (the Liquid side raised preload to metadata,
 * and with the warm-up here at 1 screen from the viewport, the asset has usually finished downloading by the time it reaches the top).
 *
 * ## smf__video--at-center — "start only once scrolled to the middle" (toggle "Start playing only when scrolled to the middle", 2026-09-28)
 *
 * Same gate, different band: released when the element reaches the middle 10% of the viewport (CENTER_GATE_MARGIN).
 * Added for the home page charging video ("Made to stay with you.") — the user wanted it to start when it's in the middle of the screen,
 * not when it merely peeks in. If both toggles are on, at-top wins (Liquid only adds one class).
 * Regression guard: tests/unit/soulog-media-feature-play-at-center.test.mjs
 *
 * ⚠️ This is the same gate as data-play-at-top in assets/soulog-bg-video.js (same rootMargin,
 * same "hand back to the regular observer once released"). That one serves the background video section, this one serves the generic media feature;
 * the two scripts are each independent IIFEs with no module system to share code, so each keeps its own implementation for now — **changing one requires aligning the other**.
 *
 * ## smf__video--click — "play only when the button is pressed" (toggle "点按钮才播放视频", 2026-10-22, Figma 1644:84)
 *
 * The video sits on its poster until the section-head button (<button data-smf-play>, e.g. "Watch the film")
 * or the video itself is clicked; then it plays from the start **with sound** (a click is a user gesture, so
 * unmuting is allowed), pressing again pauses, and when it ends it goes back to the poster (load()).
 * It still goes through the regular observer, but only for pausing: leaving the viewport pauses it like every
 * other video, and coming back **does not resume it** (2026-10-22 — the film plays with sound, so resuming on
 * scroll blasted audio at someone who was just scrolling past). It waits paused on that frame; the next press
 * continues from there. play() also refuses until the user has started it (smfUserStarted).
 * Regression guard: tests/unit/soulog-media-feature-compact.test.mjs
 *
 * Mobile source switching is handled uniformly and earlier by the global soulog-mobile-video-src.js; this file doesn't choose a source.
 * Written the same way as warm / play in assets/soulog-module-swap.js.
 * Regression guards: tests/unit/soulog-media-feature-video-lazyload.test.mjs
 *           tests/unit/soulog-media-feature-play-at-top.test.mjs
 */
(function () {
	"use strict";

	// Multiple media feature sections on the same page each include this file; the browser downloads it once but executes it multiple times.
	if (window.__soulogMediaFeatureVideo) return;
	window.__soulogMediaFeatureVideo = true;

	var SELECTOR = ".smf video";
	var LAZY_CLASS = "smf__video--lazy";
	var AT_TOP_CLASS = "smf__video--at-top";
	var AT_CENTER_CLASS = "smf__video--at-center";
	var ONCE_CLASS = "smf__video--once";
	var CLICK_CLASS = "smf__video--click";
	var IN_FULL_CLASS = "smf__video--in-full";
	var RESTART_CLASS = "smf__video--restart";
	var NEAR_MARGIN = "100% 0px";
	// The start gate's observation area is narrowed to a band covering the "top 10% of the viewport"; the element counts as at the top when its top edge enters the band.
	// When the element is taller than the viewport, this intersection interval spans a full screen, so even fast scrolling can't skip past it.
	var TOP_GATE_MARGIN = "0px 0px -90% 0px";
	// Middle-of-viewport band (45%–55%); a 10% band instead of a zero-height line for the same reason as above.
	var CENTER_GATE_MARGIN = "-45% 0px -45% 0px";

	function isLazy(video) {
		return video.classList.contains(LAZY_CLASS);
	}

	function isAtTop(video) {
		return video.classList.contains(AT_TOP_CLASS);
	}

	function isAtCenter(video) {
		return video.classList.contains(AT_CENTER_CLASS);
	}

	function isInFull(video) {
		return video.classList.contains(IN_FULL_CLASS);
	}

	function isOnce(video) {
		return video.classList.contains(ONCE_CLASS);
	}

	function isClick(video) {
		return video.classList.contains(CLICK_CLASS);
	}

	function isRestart(video) {
		return video.classList.contains(RESTART_CLASS);
	}

	/** The start gate a video goes through before the regular observer, or null */
	function gateOf(video) {
		if (isAtTop(video)) return topGate;
		if (isAtCenter(video)) return centerGate;
		if (isInFull(video)) return fullGate;
		return null;
	}

	/**
	 * smf__video--restart — "play from the start every time it enters the viewport" (toggle media_restart_on_enter, 2026-10-23).
	 * On leaving: rewind to 0 and, for gated videos, go back into the gate so the next entry waits for the same condition again.
	 * Regression guard: tests/unit/soulog-media-feature-restart-on-enter.test.mjs
	 */
	function rewind(video) {
		video.currentTime = 0;
		var gate = gateOf(video);
		if (!gate) return;
		visible.unobserve(video);
		gate.observe(video);
	}

	/** Start downloading. Must be idempotent: calling load() again on a video that's already playing jumps back to the start */
	function warm(video) {
		if (!isLazy(video) || video.dataset.smfWarm) return;
		video.dataset.smfWarm = "1";
		video.preload = "auto";
		if (video.readyState === 0) video.load();
	}

	function play(video) {
		// Videos that freeze after playing once: after ended, don't touch them again, otherwise play() seeks back to 0 and replays
		if (isOnce(video) && video.dataset.smfPlayedOnce) return;
		// Button-started videos: the observer may resume one the user started, but never start it
		if (isClick(video) && !video.dataset.smfUserStarted) return;
		// When the user scrolls faster than the warm-up, make sure it has been loaded before playing
		warm(video);
		var r = video.play();
		if (r && r.catch) r.catch(function () {});
	}

	var near = null;
	var visible = null;
	var topGate = null;
	var centerGate = null;
	var fullGate = null;

	function ensureObservers() {
		if (near) return true;
		if (!("IntersectionObserver" in window)) return false;
		near = new IntersectionObserver(
			function (entries) {
				for (var i = 0; i < entries.length; i++) {
					if (!entries[i].isIntersecting) continue;
					warm(entries[i].target);
					near.unobserve(entries[i].target);
				}
			},
			{ rootMargin: NEAR_MARGIN },
		);
		visible = new IntersectionObserver(function (entries) {
			for (var i = 0; i < entries.length; i++) {
				var target = entries[i].target;
				// Button-started videos are only ever (re)started by the user, see smf__video--click above
				if (entries[i].isIntersecting) {
					if (!isClick(target)) play(target);
				} else {
					target.pause();
					if (isRestart(target)) rewind(target);
				}
			}
		});
		/* Start gate: only responsible for "when to release the first time". Releasing hands the element to the regular observer —
		 * after observe() the browser calls back once with the current state, so play() happens naturally, and subsequent pause / resume
		 * all go through the regular path; this gate no longer takes part. */
		topGate = makeGate(TOP_GATE_MARGIN);
		centerGate = makeGate(CENTER_GATE_MARGIN);
		fullGate = makeFullGate();
		return true;
	}

	function makeGate(rootMargin) {
		var gate = new IntersectionObserver(
			function (entries) {
				for (var i = 0; i < entries.length; i++) {
					if (!entries[i].isIntersecting) continue;
					gate.unobserve(entries[i].target);
					visible.observe(entries[i].target);
				}
			},
			{ rootMargin: rootMargin },
		);
		return gate;
	}

	/**
	 * smf__video--in-full: released once the whole element is visible. A video taller than the viewport can never
	 * reach ratio 1, so "fills the viewport" also counts. Thresholds every 5% so the check gets re-run while scrolling.
	 */
	function makeFullGate() {
		var thresholds = [];
		for (var t = 0; t <= 20; t++) thresholds.push(t / 20);
		thresholds.push(0.98, 0.99);
		var gate = new IntersectionObserver(
			function (entries) {
				for (var i = 0; i < entries.length; i++) {
					var e = entries[i];
					if (!e.isIntersecting) continue;
					var fillsRoot = e.rootBounds && e.intersectionRect.height >= e.rootBounds.height * 0.98;
					if (e.intersectionRatio < 0.98 && !fillsRoot) continue;
					gate.unobserve(e.target);
					visible.observe(e.target);
				}
			},
			{ threshold: thresholds },
		);
		return gate;
	}

	/**
	 * "Play once and freeze": stop on the last frame when it reaches the end, and never replay after scrolling out of the viewport and back.
	 * Unobserving is so that even "pause() on leaving the viewport" no longer fires — pause() on an already-ended video is a no-op,
	 * but staying observed means the next time it enters the viewport play() runs again, needing the smfPlayedOnce flag as a second fallback (see play()).
	 */
	function bindOnce(video) {
		video.addEventListener("ended", function () {
			video.dataset.smfPlayedOnce = "1";
			if (visible) visible.unobserve(video);
		});
	}

	function apply(root) {
		var scope = root && root.querySelectorAll ? root : document;
		var videos = scope.querySelectorAll(SELECTOR);
		var hasIO = ensureObservers();
		for (var i = 0; i < videos.length; i++) {
			var video = videos[i];
			if (video.dataset.smfBound) continue;
			video.dataset.smfBound = "1";
			if (isOnce(video)) bindOnce(video);
			if (isClick(video)) bindClick(video);
			if (!hasIO) {
				play(video);
				continue;
			}
			if (isLazy(video)) near.observe(video);
			// Videos that start only at the top enter the gate first and are handed to the regular observer only after release (warm-up as usual; until at the top they sit on the first frame)
			(gateOf(video) || visible).observe(video);
		}
	}

	function release(root) {
		if (!near || !root || !root.querySelectorAll) return;
		var videos = root.querySelectorAll(SELECTOR);
		for (var i = 0; i < videos.length; i++) {
			near.unobserve(videos[i]);
			visible.unobserve(videos[i]);
			topGate.unobserve(videos[i]);
			centerGate.unobserve(videos[i]);
			fullGate.unobserve(videos[i]);
		}
	}

	/* ---------------------------------------------- Button-started playback */

	function buttonsFor(video) {
		var section = video.closest(".smf");
		return section ? section.querySelectorAll("[data-smf-play]") : [];
	}

	function setPressed(video, on) {
		var btns = buttonsFor(video);
		for (var i = 0; i < btns.length; i++) btns[i].setAttribute("aria-pressed", on ? "true" : "false");
	}

	function toggle(video) {
		if (!video.paused && !video.ended) {
			video.pause();
			return;
		}
		if (!video.dataset.smfUserStarted || video.ended) video.currentTime = 0;
		video.dataset.smfUserStarted = "1";
		video.muted = false;
		play(video);
	}

	function bindClick(video) {
		video.addEventListener("play", function () {
			setPressed(video, true);
		});
		video.addEventListener("pause", function () {
			setPressed(video, false);
		});
		video.addEventListener("ended", function () {
			// Back to the poster; the next press starts over
			delete video.dataset.smfUserStarted;
			video.muted = true;
			setPressed(video, false);
			video.load();
		});
		video.addEventListener("click", function () {
			toggle(video);
		});
	}

	document.addEventListener("click", function (e) {
		var btn = e.target && e.target.closest ? e.target.closest("[data-smf-play]") : null;
		if (!btn) return;
		var section = btn.closest(".smf");
		var video = section && section.querySelector("video." + CLICK_CLASS);
		if (!video) return;
		e.preventDefault();
		toggle(video);
	});

	/* ---------------------------------------------- Button-opened film (<dialog>) */

	/*
	 * smf-film — the section-head button opens a <dialog> with a *different* video (settings button_video /
	 * button_video_url, 2026-10-22: product page "Explore more use cases"). The dialog sits outside .smf on purpose,
	 * so SELECTOR (".smf video") never lazy-loads or autoplays it. Opening starts it from the top with sound
	 * (the click is a user gesture); closing — close button, Esc, or a click on the backdrop — pauses it.
	 * Regression guard: tests/unit/soulog-media-feature-film.test.mjs
	 */
	function filmVideo(dialog) {
		return dialog.querySelector("video");
	}

	function openFilm(dialog) {
		if (typeof dialog.showModal !== "function") return;
		if (!dialog.__smfFilmBound) {
			dialog.__smfFilmBound = true;
			dialog.addEventListener("close", function () {
				var v = filmVideo(dialog);
				if (v) v.pause();
			});
			dialog.addEventListener("click", function (e) {
				// Clicks on the backdrop land on the <dialog> element itself
				if (e.target === dialog || (e.target.closest && e.target.closest("[data-smf-film-close]"))) dialog.close();
			});
		}
		if (!dialog.open) dialog.showModal();
		var video = filmVideo(dialog);
		if (!video) return;
		video.preload = "auto";
		video.currentTime = 0;
		video.muted = false;
		var p = video.play();
		if (p && p.catch) p.catch(function () {});
	}

	document.addEventListener("click", function (e) {
		var btn = e.target && e.target.closest ? e.target.closest("[data-smf-film]") : null;
		if (!btn) return;
		var dialog = document.getElementById(btn.getAttribute("data-smf-film"));
		if (!dialog) return;
		e.preventDefault();
		openFilm(dialog);
	});

	apply(document);

	// The theme editor adding / removing / re-rendering a section remounts the DOM
	document.addEventListener("shopify:section:load", function (e) {
		apply(e.target || document);
	});
	document.addEventListener("shopify:section:unload", function (e) {
		release(e.target);
	});
})();
